import "./ImportClients.css";

import React, { useState } from "react";

import ErrorAlert from "shared/UI/Alerts/ErrorAlert";
import { FileUploader } from "react-drag-drop-files";
import { PageHeader } from "../../UIPalette/components/PageHeader/PageHeader";
import Button from "shared/UI/Buttons/Button";
import { ReactComponent as UploadIcon } from "shared/assets/Icons/upload.svg";
import ViewLoading from "shared/UI/Spinners/ViewLoading";
import { getServerErrorMessage } from "shared/utils/serverError";
import { useImportClientsFunctionRequest } from "shared/request/myHealthyAdvantageApi";
import { useTranslation } from "react-i18next";
import { useNotifyModalContext } from "shared/core/state/NotifyModalContext/NotifyModalContext";

export const ImportClients = () => {
  const { t } = useTranslation();
  const { modalNotify } = useNotifyModalContext();
  const [csvFile, setCsvFile] = useState<File | undefined>(undefined);
  const [clientErrorMessage, setClientErrorMessage] = useState("");
  const [serverErrorMessage, setServerErrorMessage] = useState("");
  const { trigger: importCsvFile, isMutating: importing } = useImportClientsFunctionRequest();

  const fileTypes = ["CSV"];
  const mbLimit = 10;

  const handleChange = (file: File) => {
    reset();
    setCsvFile(file);
  };

  const onSizeError = () => {
    setServerErrorMessage("");
    setClientErrorMessage(t("importClients.errors.maxFileSize", { limit: mbLimit }));
  };

  const onTypeError = () => {
    setServerErrorMessage("");
    setClientErrorMessage(t("importClients.errors.invalidFileType"));
  };

  const importOnClick = async () => {
    setServerErrorMessage("");

    const formData = new FormData();
    formData.append("csvFile", csvFile!);

    try {
      const response = await importCsvFile({ body: formData });
      const recordCount = response?.data?.recordsProcessedCount ?? 0;
      modalNotify("success", t("importClients.successTitle"), t("importClients.successMessage", { count: recordCount }));
      reset();
    } catch (error: any) {
      setServerErrorMessage(getServerErrorMessage(error, t("importClients.errors.genericServerError")));
    }
  };

  const cancelOnClick = () => {
    reset();
  };

  const reset = () => {
    setClientErrorMessage("");
    setServerErrorMessage("");
    setCsvFile(undefined);
  };

  return (
    <>
      <PageHeader title={t("importClients.title")} subtitle={t("importClients.subtitle")} />
      <h2 className="mt-5 mb-2">{t("importClients.heading")}</h2>

      <div className="w-1/2" data-testid="upload-file-container">
        {importing ? (
          <div className="p-8 border-2 border-dashed rounded-lg border-sky-grey">
            <ViewLoading containerClass="h-20" />
          </div>
        ) : (
          <FileUploader
            handleChange={handleChange}
            onSizeError={onSizeError}
            onTypeError={onTypeError}
            name="file"
            types={fileTypes}
            maxSize={mbLimit}
            classes={"fileUploader"}
          >
            <button
              className="w-full p-8 transition-all border-2 border-dashed rounded-lg border-sky-grey hover:border-black"
              data-testid="file-select-button"
            >
              {csvFile === undefined ? (
                <div className="flex flex-col justify-center align-middle">
                  <UploadIcon className={"w-full mb-6 h-[4em]"} />
                  <div className="mb-4">{t("importClients.dropZoneText")}</div>
                  <div className="text-l-grey">{t("importClients.dragZoneFileLimitations", { limit: mbLimit })}</div>
                </div>
              ) : (
                <div>{t("importClients.dragZoneFileSelected", { filename: csvFile.name })}</div>
              )}
            </button>
          </FileUploader>
        )}
        <ErrorAlert content={clientErrorMessage} />
        <ErrorAlert content={serverErrorMessage} />
      </div>

      <div className={"flex pt-5 gap-4"}>
        <Button className="max-w-[200px]" variant="secondary" text={t("importClients.cancelButton")} disabled={importing} onClick={cancelOnClick} />
        <Button
          className="max-w-[200px]"
          text={t("importClients.importButton")}
          disabled={csvFile === undefined || importing}
          onClick={importOnClick}
        />
      </div>
    </>
  );
};
